/**
 * Genesys Web Messaging v3 helpers
 *
 * In v3, member validation data is passed via customAttributes in the Database.set
 * command (or customAttributes in the messaging payload), not the old "Custom Variables."
 *
 * Configure in Genesys Cloud Admin:
 * - Web Messaging Deployment (generates Deployment ID)
 * - Messenger Configuration (bot/agent behavior)
 * - Allowed Domains (whitelist your domain or localhost)
 * - HEADLESS MODE: Enable for custom UI - the Genesys widget will NOT appear,
 *   only our WebChat component is shown. Required for useGenesysMessaging.
 */

/** Platform domain -> WebSocket region (webmessaging.<region>.pure.cloud) */
const PLATFORM_TO_WS_REGION = {
  'mypurecloud.ie': 'euw1',
  'mypurecloud.com': 'useast1',
  'mypurecloud.de': 'euc1',
  'mypurecloud.jp': 'apne1',
  'mypurecloud.com.au': 'aps1',
};

/**
 * Get WebSocket host for Genesys Web Messaging.
 * URL format: wss://webmessaging.<region>.pure.cloud/v1/app/<deploymentId>
 *
 * @param {string} region - Platform domain (mypurecloud.ie) or region code (euw1, usw2)
 * @returns {string} Host for WebSocket URL
 */
export function getGenesysWebMessagingHost(region = '') {
  const r = (region || 'mypurecloud.ie').toLowerCase();
  const wsRegion = PLATFORM_TO_WS_REGION[r] || r;
  return `webmessaging.${wsRegion}.pure.cloud`;
}

/**
 * Build customAttributes for Genesys Web Messaging v3.
 * Passes member validation data from form context when available.
 *
 * @param {Object} userInfo - { email, cellNumber, medicalAidNumber }
 * @returns {Object} customAttributes for Database.set
 */
export function getGenesysCustomAttributes(userInfo = {}) {
  return {
    PracticeDetails: 'PPN Member Service',
    Medicaloption: userInfo.medicalAidNumber ? 'Standard Plan' : '',
    MedicalAidnumber: String(userInfo.medicalAidNumber || ''),
    Email: String(userInfo.email || ''),
    CellNumber: String(userInfo.cellNumber || ''),
  };
}

const OPEN_CHAT_EVENT = 'openPPNWebChat';

/**
 * Trigger hand-off to Genesys after saving form locally.
 * Call after saveToLocalDB(formData). Opens chat and sends message with validation fields.
 *
 * @param {string} initialMessage - e.g. 'Hi - Member Validation Submitted'
 * @param {Object} customAttributes - form fields for member validation (MedicalAidNumber, PatientDOB, PracticeName, etc.)
 *
 * @example
 * const onValidationSubmit = async (formData) => {
 *   await saveToLocalDB(formData);
 *   openChatWithHandOff('Hi - Member Validation Submitted', {
 *     MedicalAidNumber: formData.MedicalAidNumber,
 *     PatientDOB: formData.PatientDOB,
 *     PracticeName: formData.PracticeName,
 *     Email: formData.Email,
 *     CellNumber: formData.CellNumber,
 *     // ... all 11 validation fields
 *   });
 * };
 */
export function openChatWithHandOff(initialMessage = 'Hi', customAttributes = {}) {
  if (typeof window === 'undefined') return;

  // Try to use official Genesys widget first
  if (window.Genesys) {
    console.log('📤 Opening Genesys Widget with form data');
    console.log('📋 Custom attributes:', customAttributes);

    // Format custom attributes for better display in Genesys
    const formattedAttributes = {};

    // Add all custom attributes
    Object.keys(customAttributes).forEach(key => {
      const value = customAttributes[key];
      // Only add non-empty values
      if (value !== null && value !== undefined && value !== '') {
        formattedAttributes[key] = String(value);
      }
    });

    // Add a timestamp and initial message if provided
    if (initialMessage) {
      formattedAttributes.InitialMessage = initialMessage;
      formattedAttributes.FormSubmissionMessage = initialMessage;
    }
    formattedAttributes.SubmittedAt = new Date().toISOString();

    console.log('✅ Sending to Genesys:', formattedAttributes);

    // Set custom attributes
    window.Genesys('command', 'Database.set', {
      messaging: {
        customAttributes: formattedAttributes,
      },
    });

    // Open the widget
    window.Genesys('command', 'Launcher.open');

    console.log('💬 Chat opened - agents can see all form data in their workspace');
    console.log('ℹ️  Note: Official Genesys widget does not support pre-populating message input');
    console.log('ℹ️  Agents will see the form data as custom attributes');

    return;
  }

  // Fallback to custom WebChat widget
  window.dispatchEvent(
    new CustomEvent(OPEN_CHAT_EVENT, {
      detail: { initialMessage, customAttributes },
    })
  );
}

/**
 * Helper function to open chat with form data from member validation
 * Formats the data nicely for Genesys agents
 * 
 * @param {Object} formData - Form data object with all fields
 * 
 * @example
 * openChatWithFormData({
 *   FormType: 'Member Validation',
 *   MedicalAidNumber: '12345',
 *   PatientName: 'John',
 *   PatientSurname: 'Doe',
 *   Email: 'john@example.com',
 *   CellNumber: '+27123456789',
 *   // ... other fields
 * });
 */
export function openChatWithFormData(formData) {
  const formType = formData.FormType || 'Member Validation';
  const message = `${formType} Form Submitted`;

  // All form data will be sent as custom attributes
  // Agents will see this in their Genesys workspace
  openChatWithHandOff(message, formData);
}
